import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency } from "@/utils/format";
import { SaldoAcumuladoStat } from "../chartUtils";
import { lineDots, lineHoverOnly } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className={`font-semibold ${item.value >= 0 ? "text-slate-800" : "text-red-600"}`}>
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    SaldoAcumuladoStat[];
  loading: boolean;
}

export function EvolucaoSaldo({ data, loading }: Props) {
  const avgSaldo = data.length > 0
    ? data.reduce((s, d) => s + d.saldo, 0) / data.length
    : 0;

  return (
    <ChartContainer
      title="Evolução do Saldo"
      subtitle="Saldo mês a mês (pagamentos, recebimentos e extrato SEMOG)"
      loading={loading}
      empty={!loading && data.length === 0}
      height={260}
    >
      <ComposedChart data={data} margin={{ top: 4, right: 60, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="mes"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={56}
          tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={56}
          tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />

        <ReferenceLine
          yAxisId="left"
          y={avgSaldo}
          stroke="#94a3b8"
          strokeDasharray="4 4"
          label={{ value: "Média", fill: "#94a3b8", fontSize: 10, position: "insideTopRight" }}
        />

        <Area
          yAxisId="left"
          type="monotone"
          dataKey="saldo"
          name="Saldo Mensal (R$)"
          stroke="#3b82f6"
          strokeWidth={2}
          fill="#3b82f620"
          {...lineDots("#3b82f6")}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="saldoAcum"
          name="Saldo Acumulado (R$)"
          stroke="#B03858"
          strokeWidth={2.5}
          dot={false}
          {...lineHoverOnly("#B03858")}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
